import type React from "react";
import { useEffect, useRef, useState } from "react";

interface MessageInputProps {
  onSendMessage: (message: string) => void;
}

function MessageInput({ onSendMessage }: MessageInputProps) {
  const [message, setMessage] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  // Auto-focus input on mount
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleSend = () => {
    const trimmedMessage = message.trim();
    if (trimmedMessage) {
      onSendMessage(trimmedMessage);
      setMessage("");
      // Return focus to input after sending
      inputRef.current?.focus();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Send message on Enter (without Shift)
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
    // Clear input on Escape
    else if (e.key === "Escape") {
      setMessage("");
      inputRef.current?.focus();
    }
  };

  return (
    <div className="flex items-stretch gap-3">
      <label htmlFor="user-input" className="sr-only">
        Type your message
      </label>
      <input
        ref={inputRef}
        type="text"
        id="user-input"
        className="/* Light mode */ /* Dark mode */ /* Responsive */ flex-1 rounded-xl border-2 border-cyan-200 bg-white px-5 py-4 text-[15px] text-teal-900 transition-all duration-200 placeholder:text-teal-400 focus:border-cyan-500 focus:shadow-[0_0_0_3px_rgba(6,182,212,0.1)] focus:outline-none sm:px-4 sm:py-3.5 sm:text-sm dark:border-slate-600 dark:bg-slate-800 dark:text-cyan-50 dark:placeholder:text-slate-400 dark:focus:shadow-[0_0_0_3px_rgba(6,182,212,0.2)]"
        placeholder="Type your message..."
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        onKeyDown={handleKeyDown}
        aria-label="Message input"
      />
      <button
        id="send-btn"
        onClick={handleSend}
        className="group /* Background gradient */ /* Shadow */ /* Hover effects */ /* Active state */ relative flex min-h-[52px] min-w-[52px] cursor-pointer items-center justify-center overflow-hidden rounded-xl bg-linear-to-br from-cyan-600 to-cyan-700 px-7 py-4 font-semibold text-white shadow-[0_4px_12px_rgba(8,145,178,0.3)] transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[0_6px_20px_rgba(8,145,178,0.4)] active:translate-y-0 dark:shadow-[0_4px_12px_rgba(8,145,178,0.4)] dark:hover:shadow-[0_6px_20px_rgba(8,145,178,0.5)]"
        aria-label="Send message"
      >
        {/* Hover overlay effect */}
        <div className="pointer-events-none absolute inset-0 bg-linear-to-br from-white/20 to-transparent opacity-0 transition-opacity duration-200 group-hover:opacity-100" />

        {/* Send arrow icon - using simple text for reliability */}
        <span className="relative z-10 text-xl font-bold">➤</span>
      </button>
    </div>
  );
}

export default MessageInput;
